import Modal from "@/components/controls/Modal";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import Tabs from "@/components/controls/Tabs";
import IconContainer from "@/components/ui/icon-container";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { Account, Close, Partners } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import CreateCompanyModal from "@/pages/companies/components/CreateCompanyModal";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { UserData } from "@/entities/users/types";
import {
  PersonData,
  PersonReferenceabilityLevel,
  PersonStatus
} from "@/entities/people/types";
import { useCreatePeopleMutation } from "@/entities/people/hooks";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { useCreatePartnerContactMutation } from "@/pages/partners/contacts/hooks";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { mapEmptyToNull } from "@/utils/formatting";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject
} from "@/utils/entityFieldsUtils/formFields";
import useBoundStore from "@/services/store/BoundStore";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { createUnsavedChangesModalSetter } from "@/entities/modals/helpers";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { useNavigate } from "react-router-dom";
import Spinner from "@/components/ui/spinner";
import { cn } from "@/utils/utils";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import DreamerModeTabs, {
  DREAMER_MODE,
  MANUAL_MODE,
  DreamerMode
} from "@/components/controls/DreamerModeTabs";
import CreatePersonWithDreamer from "./CreatePersonWithDreamer";
import { useCreatePersonWithDreamerMutation } from "@/entities/people/hooks";
import { emailRegex } from "@/utils/validation";
import { isAxiosError } from "axios";
import { useCheckCompanyDuplicates } from "@/pages/companies/hooks";
import { extractDomain, isValidWebsite } from "@/utils/domains";

type BelongsTo = "company" | "partner";

type FormValues = Record<string, unknown>;
type FormSetValue = (
  fieldName: string,
  value: unknown,
  options?: { shouldValidate?: boolean }
) => void;
type FormGetValues = () => FormValues;

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetPage?: () => void;
  companyId?: string;
  partnerId?: string;
  /** Standalone People page only — embedded callers need the synchronous `onSuccess(id)` the async Dreamer path can't give. */
  allowDreamer?: boolean;
  onSuccess?: (newId: string, name: string) => void;
  defaultFirstName?: string;
  defaultLastName?: string;
  /** Prefill from a deep link, e.g. the enrichment-failure notification's manual-create CTA. */
  defaultEmail?: string;
  onCandidateDataConsumed?: () => void;
}

const CreateContactModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  resetPage,
  companyId,
  partnerId,
  allowDreamer = false,
  onSuccess,
  defaultFirstName,
  defaultLastName,
  defaultEmail,
  onCandidateDataConsumed
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const createPerson = useCreatePeopleMutation();
  const createPartnerContact = useCreatePartnerContactMutation();
  const createWithDreamer = useCreatePersonWithDreamerMutation();
  const navigate = useNavigate();

  // `enabled` resolves async — gate the render on `loading` or the flag-OFF branch paints first and remounts the form.
  const { enabled: dreamerFlagEnabled, loading: flagLoading } = useFeatureFlag(
    FeatureFlags.CRM_8564_CREATE_PERSON_WITH_DREAMER
  );
  // allowDreamer gates both, so embedded call sites get neither the tab nor the spinner.
  const dreamerEnabled = allowDreamer && dreamerFlagEnabled;
  const dreamerFlagLoading = allowDreamer && flagLoading;
  const [activeTab, setActiveTab] = useState<DreamerMode>(DREAMER_MODE);
  const [dreamerEmail, setDreamerEmail] = useState("");
  const [showDreamerValidation, setShowDreamerValidation] = useState(false);
  const [isQueueing, setIsQueueing] = useState(false);
  const isDreamerTab = dreamerEnabled && activeTab === DREAMER_MODE;

  const [belongsTo, setBelongsTo] = useState<BelongsTo>(
    partnerId ? "partner" : "company"
  );
  const isPartner = belongsTo === "partner";
  const entityType = isPartner ? EntityType.PARTNER_CONTACT : EntityType.PERSON;

  const [formSetValue, setFormSetValue] = useState<FormSetValue | null>(null);
  const [formGetValues, setFormGetValues] = useState<FormGetValues | null>(
    null
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<"companies" | "people">("people");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");

  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);
  const [fieldResetKeys, setFieldResetKeys] = useState<Record<string, number>>(
    {}
  );

  const { data: entityFieldsResponse } = useEntityFields(entityType);
  const updateEntityField = useUpdateEntityField(entityType);

  const users = useBoundStore((state) => state.users.entities);

  // The enrichment-failure link carries only the email; its domain finds the company the failed attempt resolved.
  // isValidWebsite takes the email as is: it reads the part after "@" and rejects personal-mail and malformed domains.
  const candidateDomain =
    isOpen && !companyId && defaultEmail && isValidWebsite(defaultEmail)
      ? (extractDomain(defaultEmail) ?? "")
      : "";
  const companyDomainCheck = useCheckCompanyDuplicates(
    candidateDomain,
    candidateDomain.length > 0
  );
  // Exact domain match only, as the With Dreamer tab decides "Already in CRM".
  const domainMatchedCompanyId = candidateDomain
    ? (companyDomainCheck.data?.exactMatches?.[0]?.companyId ?? "")
    : "";
  // The form setter the domain match was last offered to: once per form, so a company the user picked is not overwritten.
  // Switching to Partner and back remounts the form, which brings a new setter and an empty company again.
  const domainPrefillSetterRef = useRef<unknown>(null);

  useEffect(() => {
    if (!domainMatchedCompanyId || isPartner) return;
    if (typeof formSetValue !== "function") return;
    if (domainPrefillSetterRef.current === formSetValue) return;
    domainPrefillSetterRef.current = formSetValue;
    if (formGetValues?.()?.companyId) return;
    setSelectedCompanyId(domainMatchedCompanyId);
    formSetValue("companyId", domainMatchedCompanyId, { shouldValidate: true });
  }, [domainMatchedCompanyId, formSetValue, formGetValues, isPartner]);

  useEffect(() => {
    if (companyId && !isPartner) {
      setSelectedCompanyId(companyId);
      if (typeof formSetValue === "function") {
        formSetValue("companyId", companyId, {
          shouldValidate: true
        });
      }
    }
  }, [companyId, formSetValue, isPartner]);

  useEffect(() => {
    if (partnerId && isPartner && typeof formSetValue === "function") {
      formSetValue("partner", partnerId, { shouldValidate: true });
    }
  }, [partnerId, formSetValue, isPartner]);

  useEffect(() => {
    if (typeof formSetValue !== "function" || isPartner) return;
    if (defaultFirstName) {
      formSetValue("firstName", defaultFirstName, { shouldValidate: true });
    }
    if (defaultLastName) {
      formSetValue("lastName", defaultLastName, { shouldValidate: true });
    }
    if (defaultEmail) {
      formSetValue("emails", [defaultEmail], { shouldValidate: true });
    }
  }, [defaultFirstName, defaultLastName, defaultEmail, formSetValue, isPartner]);

  // A deep-linked email on this route means Dreamer already failed for it, so land on Manual.
  // Gated on !isPartner like the prefill effect above: a partner contact never takes the email.
  useEffect(() => {
    if (isOpen && defaultEmail && !isPartner) {
      setActiveTab(MANUAL_MODE);
    }
  }, [isOpen, defaultEmail, isPartner]);

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: entityTypeToEntityName[entityType],
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      // Auto-select the newly created option
      if (formSetValue && typeof formGetValues === "function") {
        const isMultiSelect = currentField.displayType === "multiSelect";
        const existingValues =
          formGetValues()[currentFieldForNewOption.fieldKey];
        const safeArray = Array.isArray(existingValues) ? existingValues : [];
        const updatedSelection = isMultiSelect
          ? [...safeArray, optionName]
          : optionName;

        formSetValue(currentFieldForNewOption.fieldKey, updatedSelection, {
          shouldValidate: true
        });

        // Add a small delay before resetting the field to ensure the form value is set
        setTimeout(() => {
          setFieldResetKeys((prev) => ({
            ...prev,
            [currentFieldForNewOption.fieldKey]:
              (prev[currentFieldForNewOption.fieldKey] || 0) + 1
          }));
        }, 100);
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData: {} as UserData,
    selectedCompanyId,
    selectedPersonId,
    setSelectedCompanyId,
    setSelectedPersonId,
    setHasUserEnteredDealName: () => {},
    autoPopulateDealName: () => {},
    hasUserEnteredDealName: false,
    setView: (nextView) => {
      if (nextView === "companies" || nextView === "people") {
        setView(nextView);
      }
    },
    entityType,
    // New option creation context
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    fieldResetKeys,
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption
  };

  const baseSections = createFormNewSectionsFromAPI(
    entityFieldsResponse?.fields,
    t,
    formFieldsContext
  );

  const placeholderByFieldName: Record<string, string> = {
    firstName: t("people.createModal.firstNamePlaceholder"),
    lastName: t("people.createModal.lastNamePlaceholder"),
    emails: t("people.createModal.emailPlaceholder")
  };

  const formSections: FormSectionProps[] = baseSections.map((section) => {
    const fields = section.fields?.map((field) => {
      const isNameField =
        field.name === "firstName" || field.name === "lastName";
      const placeholderOverride = field.name
        ? placeholderByFieldName[field.name]
        : undefined;
      const enhancedField = {
        ...field,
        labelPosition: "left" as const,
        layout: isNameField ? ("half" as const) : ("flex" as const),
        ...(placeholderOverride && {
          fieldActionProps: {
            ...field.fieldActionProps,
            placeholder: placeholderOverride
          }
        }),
        ...(field.name === "firstName" && {
          label: t("people.createModal.name")
        }),
        ...(field.name === "lastName" && { label: "" })
      };

      if (!isPartner) {
        if (field.name === "companyId" && companyId) {
          enhancedField.defaultValue = companyId;
        }
        if (field.name === "firstName" && defaultFirstName) {
          enhancedField.defaultValue = defaultFirstName;
        }
        if (field.name === "lastName" && defaultLastName) {
          enhancedField.defaultValue = defaultLastName;
        }
        if (field.name === "emails" && defaultEmail) {
          enhancedField.defaultValue = [defaultEmail];
        }
      }

      return enhancedField;
    });

    if (fields) {
      const firstIdx = fields.findIndex((f) => f.name === "firstName");
      const lastIdx = fields.findIndex((f) => f.name === "lastName");
      if (firstIdx !== -1 && lastIdx !== -1 && lastIdx !== firstIdx + 1) {
        const [lastNameField] = fields.splice(lastIdx, 1);
        fields.splice(firstIdx + 1, 0, lastNameField);
      }
    }

    return { ...section, fields };
  });

  // Both tabs stay mounted, so closing checks both; distinct key so the form's own `emails` field isn't clobbered.
  const { handleCloseAttempt, warningDialogProps } = useUnsavedChanges(
    () => ({
      ...(formGetValues?.() || {}),
      ...(dreamerEmail.trim() ? { dreamerEmail } : {})
    }),
    formSections
  );

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    } else {
      formRef.current?.reset();
      setFormSetValue(null);
      setFormGetValues(null);
      setBelongsTo(partnerId ? "partner" : "company");
      setView("people");
      setSelectedCompanyId("");
      setSelectedPersonId("");
      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
      setFieldResetKeys({});
      setIsFormValid(false);
      setActiveTab(DREAMER_MODE);
      setDreamerEmail("");
      setShowDreamerValidation(false);
      setIsQueueing(false);
      domainPrefillSetterRef.current = null;
      // Drop the deep-link prefill so reopening the modal starts clean.
      onCandidateDataConsumed?.();
    }
    // onCandidateDataConsumed omitted: a fresh closure per parent render would re-run the reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, partnerId]);

  const handleBelongsToChange = (value: string) => {
    if (value === belongsTo) return;
    formRef.current?.reset();
    setFormSetValue(null);
    setFormGetValues(null);
    setSelectedCompanyId("");
    setSelectedPersonId("");
    setIsFormValid(false);
    setBelongsTo(value as BelongsTo);
  };

  // Dreamer enriches a person from a work email; a partner contact has no such flow.
  // The manual form (and its company/partner choice) is hidden while the tab is active,
  // and nothing on the Dreamer tab reads belongsTo — so a tab peek must not touch it:
  // handleBelongsToChange remounts FormNew (key={belongsTo}) and wipes typed values.
  const handleModeChange = (mode: DreamerMode) => {
    setActiveTab(mode);
  };

  // Stable backend codes; 403/404 are dead ends and must not read as transient (404 = backend flag off).
  const dreamerQueueErrorTitle = (error: unknown): string => {
    const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
    switch (detail) {
      case "user_not_provisioned":
        return t("people.createModal.withDreamer.errorNotProvisioned");
      case "invalid_email":
        return t("people.createModal.withDreamer.invalidEmail");
      case "not_found":
        return t("people.createModal.withDreamer.errorNotAvailable");
      case "person_exists":
        return t("people.createModal.withDreamer.errorPersonExists");
      default:
        return t("people.createModal.withDreamer.queueError");
    }
  };

  const handleDreamerSubmit = async () => {
    setShowDreamerValidation(true);
    const trimmedEmail = dreamerEmail.trim();
    if (!emailRegex.test(trimmedEmail) || isQueueing) {
      return;
    }
    setIsQueueing(true);
    // mutateAsync rejects even with onError supplied — unguarded, the toast never renders.
    try {
      await createWithDreamer.mutateAsync({ email: trimmedEmail });
      toast.success(
        <ToastBody title={t("people.createModal.withDreamer.queued")} />,
        { duration: 5000 }
      );
      setIsOpen(false);
    } catch (error) {
      setIsQueueing(false);
      toast.error(
        <ToastBody
          title={dreamerQueueErrorTitle(error)}
          actions={
            <IconButton
              size="md"
              variant="iconNoBg"
              iconColor="secondary"
              onClick={() => toast.dismiss()}
            >
              <Close />
            </IconButton>
          }
        />,
        { icon: null }
      );
    }
  };

  const handleSubmitPerson = async (data: PersonData) => {
    const transformFormData: PersonData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data)),
      status: PersonStatus.NEW,
      referenceability: PersonReferenceabilityLevel.UNKNOWN
    };

    await createPerson.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        resetPage?.();
        onSuccess?.(response.id, data.firstName);
        toast.success(
          <ToastBody
            title={`${data.firstName} ${data.lastName} ${t("people.createModal.success")}`}
            actions={
              <Button
                className="size-fit p-1"
                onClick={() => {
                  toast.dismiss();
                  navigate(`/people/${response?.id}`);
                }}
                variant="primary"
              >
                {t("people.createModal.view")}
              </Button>
            }
          />,
          {
            duration: 5000
          }
        );
        setIsOpen(false);
      },
      onError: () => {
        setIsSubmitting(false);
        toast.error(
          <ToastBody
            title={t("people.createModal.error")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    });
  };

  const handleSubmitPartnerContact = async (data: PartnerContactData) => {
    const transformFormData: PartnerContactData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data))
    };

    const contactFullName = getPartnerContactFullName(data);

    await createPartnerContact.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        await resetPage?.();
        onSuccess?.(response.id, contactFullName);
        toast.success(
          `${contactFullName} ${t("partnercontacts.createModal.success")}`,
          { duration: 5000 }
        );
        setIsOpen(false);
      },
      onError: () => {
        setIsSubmitting(false);
        toast.error(
          <ToastBody
            title={t("partnercontacts.createModal.error")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    });
  };

  const handleSubmit = async (data: PersonData & PartnerContactData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    if (isPartner) {
      await handleSubmitPartnerContact(data);
    } else {
      await handleSubmitPerson(data);
    }
  };

  const handleCancel = () => {
    handleCloseAttempt(() => {
      setIsOpen(false);
    });
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: {
    setValue: FormSetValue;
    getValues: FormGetValues;
  }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  const onCompanyCreation = (id: string, name: string) => {
    if (!id || !name) return;
    setSelectedCompanyId(id);
    if (typeof formSetValue === "function") {
      formSetValue("companyId", id, {
        shouldValidate: true
      });
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={createUnsavedChangesModalSetter(
          setIsOpen,
          handleCloseAttempt
        )}
        data-test="create-contact-modal"
        title={
          <Text as="div" variant="lgSubtitleH1" className="normal-case">
            {t("people.createModal.title")}
          </Text>
        }
        content={
          <div
            className={cn(
              "flex flex-col w-full p-6 overflow-y-auto",
              // g6 (24px) between the switch and the Manually form; the Dreamer tab keeps its own rhythm.
              isDreamerTab ? "gap-4" : "gap-6",
              isDreamerTab ? "max-h-[60vh]" : "h-[60vh]"
            )}
            data-test="create-contact-modal-content"
          >
            {dreamerEnabled && !dreamerFlagLoading ? (
              <DreamerModeTabs
                labels={{
                  withDreamer: t("dreamerCreate.withDreamerTab"),
                  manually: t("dreamerCreate.manuallyTab")
                }}
                value={activeTab}
                onChange={handleModeChange}
                dataTest="create-contact-modal-tabs"
              />
            ) : null}

            {dreamerFlagLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
                {isDreamerTab ? (
                  <CreatePersonWithDreamer
                    email={dreamerEmail}
                    setEmail={setDreamerEmail}
                    showValidation={showDreamerValidation}
                  />
                ) : null}
                {/* Hidden, not unmounted: remounting react-hook-form wipes typed values on a tab peek. */}
                <div
                  className={cn(
                    "flex flex-col gap-4",
                    isDreamerTab && "hidden"
                  )}
                >
            <div className="flex flex-row items-start gap-4 w-full">
              <div className="flex shrink-0 items-center w-[200px] pt-2">
                <Text variant="bodySecondary">
                  {t("people.createModal.belongsTo")}
                </Text>
                <span className="text-primary ml-1">*</span>
              </div>
              <div className="flex-1">
                <Tabs
                  fullWidth
                  size="lg"
                  options={{
                    company: (
                      <span className="flex items-center justify-center gap-1.5">
                        <IconContainer size="sm" color="inherit">
                          <Account />
                        </IconContainer>
                        {t("partnercontacts.toggle.company")}
                      </span>
                    ),
                    partner: (
                      <span className="flex items-center justify-center gap-1.5">
                        <IconContainer size="sm" color="inherit">
                          <Partners />
                        </IconContainer>
                        {t("partnercontacts.toggle.partner")}
                      </span>
                    )
                  }}
                  value={belongsTo}
                  valueSelected={handleBelongsToChange}
                  dataTest="create-contact-belongs-to-toggle"
                />
              </div>
            </div>
                  <FormNew
                    key={belongsTo}
                    sections={formSections}
                    isFormValid={(isValid) => {
                      setIsFormValid(isValid);
                    }}
                    onSubmit={handleSubmit}
                    formRef={formRef}
                    onSetValue={handleSetValue}
                    data-test="create-contact-modal-form"
                  />
                </div>
              </>
            )}
          </div>
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="create-contact-modal-footer"
          >
            <Button
              data-test="create-contact-modal-cancel-button"
              variant="secondary"
              disabled={isQueueing}
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            {/* Footer mirrors the content's loading gate — no manual submit while the flag decides the tab. */}
            {dreamerFlagLoading ? null : isDreamerTab ? (
              <Button
                data-test="create-contact-modal-dreamer-submit-button"
                disabled={isQueueing || dreamerEmail.trim().length === 0}
                onClick={handleDreamerSubmit}
              >
                {t("people.createModal.withDreamer.submit")}
              </Button>
            ) : (
              <Button
                data-test="create-contact-modal-submit-button"
                disabled={!isFormValid || isSubmitting}
                onClick={handleCreateNew}
              >
                {t("people.createModal.createNew")}
              </Button>
            )}
          </div>
        }
        contentContainerClassname="w-[640px]"
      />
      <CreateCompanyModal
        isOpen={view === "companies"}
        setIsOpen={(isCompanyModalOpen) => {
          if (!isCompanyModalOpen) {
            setView("people");
          }
        }}
        onSuccess={onCompanyCreation}
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        onCreateOption={handleCreateNewOption}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
        data-test="create-contact-modal-new-option"
      />
      <UnsavedChangesWarningDialog
        {...warningDialogProps}
        data-test="create-contact-modal-unsaved-warning"
      />
    </>
  );
};

export default CreateContactModal;
